/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useMemo } from '@web-stories-wp/react';
import { isPatternEqual } from '@web-stories-wp/patterns';

/**
 * Internal dependencies
 */
import { MULTIPLE_VALUE } from '../../../constants';

/**
 * Memoizes the common color value.
 *
 * @param {Array.<Object>} list List of objects.
 * @param {string} property Property to check on all objects.
 * @return {Object} Found common color value.
 */
function useCommonColorValue(list, property) {
  return useMemo(() => {
    const allValues = list.map((element) => element[property]);
    if (allValues.length === 0) {
      return null;
    }
    if (allValues.length === 1) {
      return allValues[0];
    }
    return allValues.every((value) => isPatternEqual(value, allValues[0]))
      ? allValues[0]
      : MULTIPLE_VALUE;
  }, [list, property]);
}

export default useCommonColorValue;
